import type {
  BellNotification,
  NotificationSeverity,
} from "$global-providers/notifications-provider";
import { Chip, Drawer } from "@material-ui/core";
import { styled, type Theme, useTheme } from "@material-ui/core/styles";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, type IntlShape, useIntl } from "react-intl";
import { Link as RouterLink } from "react-router-dom";

export type NotificationPanelProps = {
  open: boolean;
  onClose: () => void;
  notifications: BellNotification[];
  onMarkAllRead: () => Promise<void> | void;
  onDismiss: (key: string, version: string | null) => Promise<void> | void;
  /**
   * Called when the user clicks a notification's primary surface (Link or
   * button) — the bell uses this to bump its read watermark so an unread row
   * that the user just acted on doesn't keep the badge lit.
   */
  onActivate: () => Promise<void> | void;
};

type FilterId = "all" | "actionRequired" | "info";

const FILTERS: { id: FilterId; messageId: string; defaultMessage: string }[] = [
  { id: "all", messageId: "notifications.filter.all", defaultMessage: "All" },
  {
    id: "actionRequired",
    messageId: "notifications.filter.actionRequired",
    defaultMessage: "Action required",
  },
  {
    id: "info",
    messageId: "notifications.filter.info",
    defaultMessage: "Info",
  },
];

const Header = styled("div")(({ theme }) => ({
  padding: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
}));

const Title = styled("div")(({ theme }) => ({
  fontSize: 16,
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

const MarkAllButton = styled("button")(({ theme }) => ({
  fontSize: 12,
  fontWeight: 500,
  color: theme.palette.brand.main,
  background: "transparent",
  border: "none",
  cursor: "pointer",
  padding: 0,
  "&:disabled": {
    color: theme.palette.grey[400],
    cursor: "default",
  },
}));

const FiltersRow = styled("div")(({ theme }) => ({
  padding: theme.spacing(1.25, 2),
  display: "flex",
  gap: theme.spacing(0.75),
  overflowX: "auto",
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  backgroundColor: theme.palette.grey[50],
}));

const FilterChip = styled(Chip)({
  flexShrink: 0,
});

const EmptyState = styled("div")(({ theme }) => ({
  padding: theme.spacing(5, 2),
  textAlign: "center",
  fontSize: 13,
  color: theme.palette.grey[500],
}));

const severityAccent = (theme: Theme, severity: NotificationSeverity) =>
  severity === "ACTION_REQUIRED"
    ? theme.palette.error.main
    : theme.palette.brand.main;

const itemStyles = (
  theme: Theme,
  $unread: boolean,
  $severity: NotificationSeverity,
) => ({
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  display: "flex",
  gap: theme.spacing(1.25),
  cursor: "pointer",
  position: "relative" as const,
  backgroundColor: $unread ? "#FDFEFF" : theme.palette.common.white,
  textAlign: "left" as const,
  textDecoration: "none",
  color: "inherit",
  border: "none",
  width: "100%",
  fontFamily: "inherit",
  fontSize: "inherit",
  "&:last-child": { borderBottom: "none" },
  "&:hover": { backgroundColor: theme.palette.grey[50] },
  "&::before": $unread
    ? {
        content: '""',
        position: "absolute" as const,
        left: 0,
        top: 0,
        bottom: 0,
        width: 3,
        borderRadius: "0 2px 2px 0",
        backgroundColor: severityAccent(theme, $severity),
      }
    : {},
});

const NotificationItemLink = styled(RouterLink)<
  Theme,
  { $unread: boolean; $severity: NotificationSeverity }
>(({ theme, $unread, $severity }) => itemStyles(theme, $unread, $severity));

const NotificationItemButton = styled("button")<
  Theme,
  { $unread: boolean; $severity: NotificationSeverity }
>(({ theme, $unread, $severity }) => itemStyles(theme, $unread, $severity));

const ItemIcon = styled("div")<Theme, { $severity: NotificationSeverity }>(
  ({ $severity }) => ({
    width: 32,
    height: 32,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 16,
    flexShrink: 0,
    backgroundColor: $severity === "ACTION_REQUIRED" ? "#FEE8E7" : "#E6F7F8",
  }),
);

const ItemBody = styled("div")({
  flex: 1,
  minWidth: 0,
});

const ItemTitle = styled("div")(({ theme }) => ({
  fontSize: 13,
  fontWeight: 600,
  color: theme.palette.text.primary,
  lineHeight: 1.4,
  marginBottom: 2,
}));

const ItemSub = styled("div")(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.grey[600],
  lineHeight: 1.5,
}));

const ItemTime = styled("div")(({ theme }) => ({
  fontSize: 11,
  color: theme.palette.grey[400],
  marginTop: 4,
}));

const ItemAction = styled("span")(({ theme }) => ({
  display: "block",
  fontSize: 12,
  fontWeight: 600,
  color: theme.palette.brand.main,
  marginTop: 5,
}));

const UnreadDot = styled("span")(({ theme }) => ({
  width: 7,
  height: 7,
  borderRadius: "50%",
  backgroundColor: theme.palette.brand.main,
  flexShrink: 0,
  marginTop: 6,
}));

// Each row is a containing div with the clickable area (Link/Button) plus a
// sibling DismissButton overlaying its top-right corner. The two are siblings
// — never nested — so the row stays valid HTML and the dismiss control is
// independently keyboard-focusable.
const RowWrapper = styled("div")({
  position: "relative",
});

const DismissButton = styled("button")(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(0.5),
  right: theme.spacing(0.5),
  width: 22,
  height: 22,
  borderRadius: 11,
  border: "none",
  background: "transparent",
  color: theme.palette.grey[500],
  cursor: "pointer",
  fontSize: 14,
  lineHeight: 1,
  padding: 0,
  zIndex: 1,
  "&:hover": {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.grey[700],
  },
}));

const severityGlyph = (severity: NotificationSeverity) =>
  severity === "ACTION_REQUIRED" ? "⚠" : "•";

const formatRelativeTime = (intl: IntlShape, iso: string): string => {
  const date = new Date(iso);
  const now = new Date();
  const time = intl.formatTime(date, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const isSameDay = date.toDateString() === now.toDateString();
  if (isSameDay) {
    return intl.formatMessage(
      { id: "notifications.time.today", defaultMessage: "Today · {time}" },
      { time },
    );
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return intl.formatMessage(
      {
        id: "notifications.time.yesterday",
        defaultMessage: "Yesterday · {time}",
      },
      { time },
    );
  }
  const datePart = intl.formatDate(date, { day: "2-digit", month: "short" });
  return intl.formatMessage(
    {
      id: "notifications.time.older",
      defaultMessage: "{date} · {time}",
    },
    { date: datePart, time },
  );
};

const DEFAULT_HEADER_HEIGHT = 64;

const matchesFilter = (n: BellNotification, filter: FilterId) => {
  if (filter === "all") return true;
  if (filter === "actionRequired") return n.severity === "ACTION_REQUIRED";
  return n.severity === "INFO";
};

export const NotificationPanel: FC<NotificationPanelProps> = ({
  open,
  onClose,
  notifications,
  onMarkAllRead,
  onDismiss,
  onActivate,
}) => {
  const handleActivate = () => {
    void onActivate();
    onClose();
  };
  const intl = useIntl();
  const theme = useTheme();
  const [activeFilter, setActiveFilter] = useState<FilterId>("all");
  const [headerHeight, setHeaderHeight] = useState<number>(
    DEFAULT_HEADER_HEIGHT,
  );

  // Reset the active filter chip when the panel hides so the next open
  // starts from "All" — avoids a stale filter that hides relevant rows.
  useEffect(() => {
    if (!open) setActiveFilter("all");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const measure = () => {
      const header = document.querySelector<HTMLElement>(
        '[data-testid="GlobalHeader"]',
      );
      if (header) setHeaderHeight(header.getBoundingClientRect().height);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open]);

  const { unreadCounts, totalCounts } = useMemo(() => {
    const total = (f: FilterId) =>
      notifications.filter((n) => matchesFilter(n, f)).length;
    const unread = (f: FilterId) =>
      notifications.filter((n) => n.unread && matchesFilter(n, f)).length;
    return {
      unreadCounts: {
        all: unread("all"),
        actionRequired: unread("actionRequired"),
        info: unread("info"),
      } as Record<FilterId, number>,
      totalCounts: {
        all: total("all"),
        actionRequired: total("actionRequired"),
        info: total("info"),
      } as Record<FilterId, number>,
    };
  }, [notifications]);

  const visibleFilters = FILTERS.filter(
    (f) => f.id === "all" || totalCounts[f.id] > 0,
  );

  // If the currently-selected chip has just been removed from the row (e.g. the
  // last action-required notification was dismissed while the panel is open),
  // fall back to "all" so we never paint an empty body under a hidden chip.
  useEffect(() => {
    if (!visibleFilters.some((f) => f.id === activeFilter)) {
      setActiveFilter("all");
    }
  }, [visibleFilters, activeFilter]);

  const filtered = notifications.filter((n) => matchesFilter(n, activeFilter));
  const hasUnread = notifications.some((n) => n.unread);

  return (
    <Drawer
      anchor="top"
      open={open}
      onClose={onClose}
      data-testid="notifications-panel"
      ModalProps={{
        // Stack above the right-hand sidebar (zIndex.drawer = 1200) and the
        // page's fixed bottom action bar (zIndex.drawer + 1 = 1201) so the
        // panel and its shadow cover them. pointerEvents: "none" on the
        // modal root lets clicks pass through the area above the backdrop
        // (where the brand-coloured top nav sits), keeping the header
        // interactive while the panel is open.
        style: {
          zIndex: theme.zIndex.drawer + 2,
          pointerEvents: "none",
        },
      }}
      BackdropProps={{
        style: { top: headerHeight, pointerEvents: "auto" },
      }}
      PaperProps={{
        style: {
          top: headerHeight,
          left: "auto",
          right: 0,
          width: "100%",
          maxWidth: 480,
          backgroundColor: theme.palette.common.white,
          borderBottom: `1px solid ${theme.palette.grey[200]}`,
          pointerEvents: "auto",
        },
      }}
    >
      <Header>
        <Title>
          <FormattedMessage
            id="notifications.panel.title"
            defaultMessage="Notifications"
          />
        </Title>
        <MarkAllButton
          type="button"
          disabled={!hasUnread}
          onClick={() => void onMarkAllRead()}
          data-testid="notifications-mark-all-read"
        >
          <FormattedMessage
            id="notifications.panel.markAllRead"
            defaultMessage="Mark all read"
          />
        </MarkAllButton>
      </Header>

      <FiltersRow>
        {visibleFilters.map((f) => {
          const isActive = activeFilter === f.id;
          const labelText = intl.formatMessage({
            id: f.messageId,
            defaultMessage: f.defaultMessage,
          });
          const countSuffix =
            unreadCounts[f.id] > 0 ? ` (${unreadCounts[f.id]})` : "";
          return (
            <FilterChip
              key={f.id}
              size="small"
              clickable
              label={`${labelText}${countSuffix}`}
              color={isActive ? "primary" : "default"}
              variant={isActive ? "default" : "outlined"}
              onClick={() => setActiveFilter(f.id)}
            />
          );
        })}
      </FiltersRow>

      {filtered.length === 0 ? (
        <EmptyState>
          <FormattedMessage
            id="notifications.panel.empty"
            defaultMessage="You're all caught up."
          />
        </EmptyState>
      ) : (
        filtered.map((n) => {
          const handleDismiss = () => {
            void onDismiss(n.key, n.version ?? null);
          };
          const inner = (
            <>
              <ItemIcon $severity={n.severity} aria-hidden="true">
                {n.icon ?? severityGlyph(n.severity)}
              </ItemIcon>
              <ItemBody>
                <ItemTitle>{n.title}</ItemTitle>
                {n.body && <ItemSub>{n.body}</ItemSub>}
                <ItemTime>{formatRelativeTime(intl, n.createdAt)}</ItemTime>
                {n.actionUrl && (
                  <ItemAction>
                    <FormattedMessage
                      id="notifications.panel.openAction"
                      defaultMessage="Open →"
                    />
                  </ItemAction>
                )}
              </ItemBody>
              {n.unread && <UnreadDot />}
            </>
          );
          return (
            <RowWrapper key={n.id}>
              {n.actionUrl ? (
                <NotificationItemLink
                  to={n.actionUrl}
                  $unread={n.unread}
                  $severity={n.severity}
                  data-testid={`notification-item-${n.id}`}
                  onClick={handleActivate}
                >
                  {inner}
                </NotificationItemLink>
              ) : (
                <NotificationItemButton
                  type="button"
                  $unread={n.unread}
                  $severity={n.severity}
                  data-testid={`notification-item-${n.id}`}
                  onClick={handleActivate}
                >
                  {inner}
                </NotificationItemButton>
              )}
              <DismissButton
                type="button"
                aria-label={intl.formatMessage(
                  {
                    id: "notifications.panel.dismissWithTitle",
                    defaultMessage: "Dismiss {title}",
                  },
                  { title: n.title },
                )}
                data-testid={`notification-item-dismiss-${n.id}`}
                onClick={handleDismiss}
              >
                ×
              </DismissButton>
            </RowWrapper>
          );
        })
      )}
    </Drawer>
  );
};
